import React, { useMemo } from 'react';
import { matchPath, Route, Routes, useLocation } from 'react-router-dom';
import { GlobalPageLoadExperience } from '@atlassian/ufo';

import CapabilityFocus from '@/views/capability-view';
import { CapabilityTabNames } from '@/views/capability-view/constants';
import HomepageView from '@/views/homepage-view';
import SearchView from '@/views/search-view';
import SLOView from '@/views/slo-view';
import SLOGroupView from '@/views/slo-group-view';
import SLOMimirView from '@/views/slo-mimir-view';
import Dashboard from '@/views/dashboard';
import ProductView from '@/views/product-view';
import TeamView from '@/views/team-view';
import SimulationView from '@/views/simulation-view';
import { PageNotFound } from '@/components/primitive';
import { PageContentTabNames } from '@/components/composed/page-content-tabs/constants';
import { ExperienceNames } from '@/services/slo-service/constants';

// NOTE: These pages must have at least one PageSegmentLoad instrumentation
//  in order for the experience to be measured.
// In v6, routes order does not matter anymore
const routes = [
  {
    path: '/simulate',
    experience: ExperienceNames.SIMULATION_PAGE,
    component: <SimulationView />,
  },
  {
    path: '/search',
    experience: ExperienceNames.SEARCH_PAGE,
    component: <SearchView />,
  },
  {
    path: '/capability/:id/history',
    experience: ExperienceNames.CAPABILITY_HISTORY_PAGE,
    component: <CapabilityFocus tabName={CapabilityTabNames.HISTORY} />,
  },
  {
    path: '/capability/:id',
    experience: ExperienceNames.CAPABILITY_PAGE,
    component: <CapabilityFocus />,
  },
  {
    path: '/slo/:publicId',
    experience: ExperienceNames.SLO_PAGE,
    component: <SLOView />,
  },
  {
    path: '/slo-group/:publicId',
    experience: ExperienceNames.SLO_GROUP_PAGE,
    component: <SLOGroupView />,
  },
  {
    path: '/slo-group/:publicId/configuration',
    experience: ExperienceNames.SLO_GROUP_CONFIGURATION_PAGE,
    component: <SLOGroupView tabName={PageContentTabNames.CONFIGURATION} />,
  },
  {
    path: '/slo-group/:publicId/history',
    experience: ExperienceNames.SLO_GROUP_HISTORY_PAGE,
    component: <SLOGroupView tabName={PageContentTabNames.HISTORY} />,
  },
  {
    path: '/slo-group/:publicId/sync',
    experience: ExperienceNames.SLO_GROUP_HISTORY_PAGE,
    component: <SLOGroupView tabName={PageContentTabNames.SYNC} />,
  },
  {
    path: '/slo-mimir/:publicId',
    experience: ExperienceNames.SLO_MIMIR_PAGE,
    component: <SLOMimirView />,
  },
  {
    path: '/slo-mimir/:publicId/configuration',
    experience: ExperienceNames.SLO_MIMIR_CONFIGURATION_PAGE,
    component: <SLOMimirView tabName={PageContentTabNames.CONFIGURATION} />,
  },
  {
    path: '/slo-mimir/:publicId/history',
    experience: ExperienceNames.SLO_MIMIR_HISTORY_PAGE,
    component: <SLOMimirView tabName={PageContentTabNames.HISTORY} />,
  },
  {
    path: '/team/:publicId/dashboard/products',
    experience: ExperienceNames.TEAM_PRODUCTS_PAGE,
    component: <TeamView tabName={PageContentTabNames.PRODUCTS} />,
  },
  {
    path: '/team/:publicId/dashboard/history',
    experience: ExperienceNames.TEAM_HISTORY_PAGE,
    component: <TeamView tabName={PageContentTabNames.HISTORY} />,
  },
  {
    path: '/team/:publicId/dashboard',
    experience: ExperienceNames.TEAM_PAGE,
    component: <TeamView />,
  },
  {
    path: '/team/:publicId/history',
    experience: ExperienceNames.TEAM_HISTORY_PAGE,
    component: <TeamView tabName={PageContentTabNames.HISTORY} />,
  },
  {
    path: '/team/:publicId',
    experience: ExperienceNames.TEAM_PAGE,
    component: <TeamView />,
  },
  {
    path: '/product/:publicId/dashboard/teams',
    experience: ExperienceNames.PRODUCT_TEAMS_PAGE,
    component: <ProductView tabName={PageContentTabNames.TEAMS} />,
  },
  {
    path: '/product/:publicId/dashboard/history',
    experience: ExperienceNames.PRODUCT_HISTORY_PAGE,
    component: <ProductView tabName={PageContentTabNames.HISTORY} />,
  },
  {
    path: '/product/:publicId/dashboard',
    experience: ExperienceNames.PRODUCT_PAGE,
    component: <ProductView />,
  },
  {
    path: '/product/:publicId/history',
    experience: ExperienceNames.PRODUCT_HISTORY_PAGE,
    component: <ProductView tabName={PageContentTabNames.HISTORY} />,
  },
  {
    path: '/product/:publicId',
    experience: ExperienceNames.PRODUCT_PAGE,
    component: <ProductView />,
  },
  {
    path: '/catalog',
    experience: ExperienceNames.LANDING_PAGE,
    component: <Dashboard />,
  },
  {
    path: '/',
    experience: ExperienceNames.HOMEPAGE,
    component: <HomepageView />,
  },
];

let initialLoad = true;

const PageRoutes = () => {
  const location = useLocation();

  // Only execute the startPageLoad once if the location changes
  // https://bitbucket.org/atlassian/onboard/src/master/src/utils/route-listener/index.tsx
  useMemo(() => {
    if (
      // Not in any initial or final states
      GlobalPageLoadExperience.state.final !== true
    ) {
      // Transition from STARTED to STARTED is not allowed
      // Abort any existing experience in progress to ensure we can start again
      // 'force: true' means transition state even though 'until' is defined
      // noinspection JSIgnoredPromiseFromCall
      GlobalPageLoadExperience.abort({ force: true });
    }

    for (let i = 0; i < routes.length; ++i) {
      const { experience, path } = routes[i];
      if (matchPath({ path, caseSensitive: true, end: true }, location.pathname)) {
        // Start time is taken at the time of promise creation
        // This finishes automatically once all product PageSegmentLoads have finished
        // noinspection JSIgnoredPromiseFromCall
        GlobalPageLoadExperience.startPageLoad(experience, initialLoad);
        break;
      }
    }

    initialLoad = false;
  }, [location.pathname]);

  return (
    <Routes>
      {routes.map(({ path, component }) => (
        <Route key={path} path={path} element={component} />
      ))}
      {/* NOTE: Handle unfound routes (404) */}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default PageRoutes;
